import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { xtreamApi } from '../services/xtreamApi';
import HeroCarousel from '../components/Home/HeroCarousel';
import Top10Section from '../components/Home/Top10Section';
import ContentRow from '../components/Home/ContentRow';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { Play, Clock, RefreshCw } from 'lucide-react';

const ContinueWatchingRow = ({ items }) => {
  if (!items || items.length === 0) return null;

  return (
    <section data-testid="continue-watching-section" className="py-6">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Clock style={{color:"var(--accent)"}} className="w-5 h-5" />
          Continue Watching
        </h2>
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide scroll-smooth">
          {items.map((item) => {
            const linkPath = item.content_type === 'series'
              ? `/series/${item.content_id}`
              : `/movie/${item.content_id}`;
            return (
              <Link
                key={`${item.content_type}-${item.content_id}-${item.episode_id || ''}`}
                to={linkPath}
                className="flex-shrink-0 group"
                data-testid={`continue-watching-${item.content_id}`}
              >
                <div className="relative w-52 rounded-lg overflow-hidden bg-gray-900">
                  <div className="aspect-video bg-gray-800 relative">
                    <img
                      src={item.poster}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/208x117?text=No+Image'; }}
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
                      <div style={{backgroundColor:"var(--accent)"}} className="h-full" style={{ width: `${item.progress_percent}%` }} />
                    </div>
                  </div>
                  <div className="p-2">
                    <h3 className="text-white text-sm font-medium line-clamp-1">{item.name}</h3>
                    {item.season && item.episode && (
                      <p className="text-gray-500 text-xs mt-0.5">S{item.season}E{item.episode}</p>
                    )}
                    <p className="text-gray-500 text-xs mt-0.5">{Math.round(item.progress_percent)}% watched</p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const [recentMovies, setRecentMovies] = useState([]);
  const [recentSeries, setRecentSeries] = useState([]);
  const [continueWatching, setContinueWatching] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchContent = useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      setIsRefreshing(true);

      const [movieCategories, seriesCategories, watchProgress] = await Promise.all([
        xtreamApi.getVodCategories().catch(() => []),
        xtreamApi.getSeriesCategories().catch(() => []),
        xtreamApi.getWatchProgress().catch(() => [])
      ]);

      setContinueWatching(Array.isArray(watchProgress) ? watchProgress : []);

      let movies = [];
      if (movieCategories && movieCategories.length > 0) {
        const recentCat = movieCategories.find(c => 
          c.category_name.toLowerCase().includes('recent') || 
          c.category_name.toLowerCase().includes('new') ||
          c.category_name.toLowerCase().includes('2025') ||
          c.category_name.toLowerCase().includes('2026')
        ) || movieCategories[0];
        movies = await xtreamApi.getVodStreams(recentCat.category_id).catch(() => []);
      }

      let series = [];
      if (seriesCategories && seriesCategories.length > 0) {
        const recentSeriesCat = seriesCategories.find(c => 
          c.category_name.toLowerCase().includes('netflix') || 
          c.category_name.toLowerCase().includes('hbo') ||
          c.category_name.toLowerCase().includes('new')
        ) || seriesCategories[0];
        series = await xtreamApi.getSeries(recentSeriesCat.category_id).catch(() => []);
      }

      setRecentMovies(Array.isArray(movies) ? movies.slice(0, 20) : []);
      setRecentSeries(Array.isArray(series) ? series.slice(0, 20) : []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => { fetchContent(); }, [fetchContent]);

  // Auto-refresh every hour
  useEffect(() => {
    const interval = setInterval(() => fetchContent(true), 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchContent]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading content..." />
      </div>
    );
  }

  const heroContent = recentMovies.slice(0, 5).map((movie) => ({
    id: movie.stream_id,
    title: movie.name,
    description: movie.plot || 'No description available',
    backdrop: movie.stream_icon,
    logo: null,
    rating: movie.rating || '0',
    year: movie.year || new Date().getFullYear().toString(),
    genres: movie.genre ? movie.genre.split(',').map(g => g.trim()) : ['Movie'],
    type: 'movie',
    streamId: movie.stream_id
  }));

  const top10Content = recentMovies.slice(0, 10).map((movie, index) => ({
    rank: index + 1,
    id: movie.stream_id,
    title: movie.name,
    poster: movie.stream_icon,
    type: 'movie'
  }));

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#0a0a0b]">
      {heroContent.length > 0 && <HeroCarousel slides={heroContent} />}

      {/* Refresh bar */}
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 pt-4 flex items-center justify-end gap-3">
        {lastRefresh && (
          <span className="text-gray-600 text-xs">
            Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <button
          data-testid="refresh-content-btn"
          onClick={() => fetchContent(true)}
          disabled={isRefreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {/* Continue Watching */}
      <ContinueWatchingRow items={continueWatching} />

      {top10Content.length > 0 && <Top10Section items={top10Content} />}

      {recentMovies.length > 0 && (
        <ContentRow title="Recently Added Movies" items={recentMovies} type="movie" />
      )}

      {recentSeries.length > 0 && (
        <ContentRow title="Popular Series" items={recentSeries} type="series" />
      )}

      {recentMovies.length === 0 && recentSeries.length === 0 && (
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-400">No content available</p>
        </div>
      )}
    </div>
  );
};

export default HomePage;
